import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { get } from './api';
import { applyAppIcon, applyBrand } from './brand';
import type { Locale } from './types';

export interface TenantInfo {
  kind: 'platform' | 'school' | 'unknown';
  platformName: string;
  platformDomain: string | null;
  platformUrl: string;
  demoEnabled: boolean;
  demoMinutes?: number;
  school: {
    slug: string;
    name: string;
    nameAr: string | null;
    logoUrl: string | null;
    brandColor: string | null;
    welcomeMessage: string | null;
    welcomeMessageAr: string | null;
    status: string;
    defaultLocale: Locale;
    isDemo: boolean;
  } | null;
}

const Ctx = createContext<TenantInfo | null>(null);

/** Which address the visitor is on: the platform (landing page) or a school (its own sign-in) */
export function TenantProvider({ children }: { children: ReactNode }) {
  const [t, setT] = useState<TenantInfo | null>(null);
  useEffect(() => {
    get<TenantInfo>('/api/public/tenant')
      .then((info) => {
        // On a school's address, its colour and icon apply from the first screen
        applyBrand(info.school?.brandColor);
        applyAppIcon(info.school?.logoUrl, info.school ? (info.school.defaultLocale === 'ar' && info.school.nameAr) || info.school.name : info.platformName);
        setT(info);
      })
      .catch(() => setT({ kind: 'platform', platformName: 'SignMeOut', platformDomain: null, platformUrl: location.origin, demoEnabled: false, school: null }));
  }, []);
  if (!t) return <div className="splash" />;
  return <Ctx.Provider value={t}>{children}</Ctx.Provider>;
}

export function useTenant() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTenant outside TenantProvider');
  return v;
}
